import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faArrowRight,
  faChevronDown,
  faRotateRight,
  faTriangleExclamation,
} from "@fortawesome/free-solid-svg-icons";
import clsx from "clsx";
import moment from "moment";
import axiosInstance from "../../utils/axiosInstance";
import { debtSyncStepLabels } from "../../utils/data/chase-logs-data";

type SyncFailure = {
  id: number;
  chaseId: number;
  syncStep: string | null;
  attempt: number;
  createdAt: string;
  loan: { id: number; policyReferenceNumber: string };
};

type Props = {
  className?: string;
};

/** Stays on top of the dashboard and chase list while any V2 plan sync to CDL is still broken. */
export default function DebtSyncAlert({ className }: Props) {
  const navigate = useNavigate();
  const [failures, setFailures] = useState<SyncFailure[]>([]);
  const [count, setCount] = useState(0);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const getFailures = async () => {
      try {
        const res = await axiosInstance.get("/loan/debt-plan-sync/open");
        setFailures(res.data.data ?? []);
        setCount(res.data.count ?? 0);
      } catch (error) {
        // the alert is extra, a failed check shouldn't block the page
        console.log(error);
      }
    };

    getFailures();
  }, []);

  if (!count) return null;

  return (
    <section
      className={clsx(
        "relative overflow-hidden rounded-2xl border border-rose-500/20 bg-surface shadow-card animate-fade-in",
        className,
      )}
    >
      {/* Soft rose wash and a top highlight give the card its glass feel */}
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0 bg-gradient-to-br from-rose-500/[0.12] via-rose-500/[0.04] to-transparent"
      />
      <div
        aria-hidden
        className="pointer-events-none absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-rose-400/60 to-transparent"
      />

      <button
        type="button"
        aria-expanded={open}
        onClick={() => setOpen(!open)}
        className="relative w-full flex items-center gap-4 px-5 py-4 max-sm:px-4 text-start backdrop-blur-sm"
      >
        <span className="relative shrink-0 w-11 h-11 rounded-xl flex items-center justify-center text-white bg-gradient-to-br from-rose-500 to-red-600 shadow-lg shadow-rose-500/30">
          <FontAwesomeIcon icon={faTriangleExclamation} />
          <span className="absolute -top-1 -right-1 flex h-3 w-3">
            <span className="absolute inline-flex h-full w-full rounded-full bg-rose-400 opacity-75 animate-ping" />
            <span className="relative inline-flex h-3 w-3 rounded-full bg-rose-500 border-2 border-[color:var(--color-surface)]" />
          </span>
        </span>

        <span className="flex-1 min-w-0">
          <span className="block text-[15px] font-bold text-text-primary leading-snug">
            {count} {count === 1 ? "chase is" : "chases are"} out of sync with CDL
          </span>
          <span className="block mt-0.5 text-[13px] font-medium text-light-gray">
            A plan or payment didn't reach CDL. Open the chase to retry, or let an admin know.
          </span>
        </span>

        <span className="shrink-0 flex items-center gap-2 h-9 px-4 rounded-full text-[13px] font-semibold text-rose-600 dark:text-rose-300 bg-rose-500/10 border border-rose-500/20 hover:bg-rose-500/15 transition-colors duration-200 max-sm:px-3">
          <span className="max-sm:hidden">{open ? "Hide" : "Review"}</span>
          <FontAwesomeIcon
            icon={faChevronDown}
            className={clsx("text-[11px] transition-transform duration-300", open && "rotate-180")}
          />
        </span>
      </button>

      {open ? (
        <div className="relative px-3 pb-3 max-sm:px-2 animate-fade-in">
          <div className="rounded-xl border border-edge-faint bg-overlay divide-y divide-edge-faint">
            {failures.map((item) => (
              <button
                type="button"
                key={item.id}
                onClick={() =>
                  navigate(`/chaselist?query=${item.loan.policyReferenceNumber}`)
                }
                className="group w-full flex flex-wrap items-center gap-x-4 gap-y-1.5 px-4 py-3 text-start hover:bg-overlay-hover transition-colors duration-200 first:rounded-t-xl last:rounded-b-xl"
              >
                <span className="text-[13px] font-semibold text-text-primary tabular-nums">
                  {item.loan.policyReferenceNumber}
                </span>
                <span className="text-[12px] font-medium text-text-secondary px-2.5 py-[3px] rounded-full border border-edge bg-surface">
                  {debtSyncStepLabels[item.syncStep ?? ""] ?? "Unknown step"}
                </span>
                <span className="flex-1" />
                {item.attempt ? (
                  <span className="flex items-center gap-1.5 text-[12px] font-medium text-light-gray tabular-nums">
                    <FontAwesomeIcon icon={faRotateRight} className="text-[10px]" />
                    {item.attempt} {item.attempt === 1 ? "retry" : "retries"}
                  </span>
                ) : null}
                <span
                  className="text-[12px] font-medium text-light-gray"
                  title={moment(item.createdAt).format("DD/MM/YYYY HH:mm")}
                >
                  {moment(item.createdAt).fromNow()}
                </span>
                <FontAwesomeIcon
                  icon={faArrowRight}
                  className="text-[11px] text-light-gray group-hover:text-text-primary group-hover:translate-x-0.5 transition-all duration-200"
                />
              </button>
            ))}
          </div>

          {count > failures.length ? (
            <p className="px-2 pt-2 text-[12px] text-light-gray">
              Showing the newest {failures.length} of {count}
            </p>
          ) : null}
        </div>
      ) : null}
    </section>
  );
}
